import { useGetObjectLimitQuery } from '../api/inventory/inventoryApiSlice';

interface ObjectLimitState {
    /** Rooms + stations together, as every limit counts them (PO, 2026-11-02). */
    current: number;
    /** Maximum allowed objects for the tenant's plan */
    max: number;
    /** Whether the tenant is at or over the limit */
    isAtLimit: boolean;
    /** Whether the tenant is on a trial plan (limit enforcement applies) */
    isTrial: boolean;
    /**
     * WHAT is being counted — the banner has to say so, and it differs by
     * tenant: stations for a station tenant, rooms for a room/venue one.
     * Until 2026-09-14 both were labelled "Objekte", which is the word this
     * admin uses for a THIRD thing entirely (an asset — a compartment, a
     * room). So an operator with one station of nine compartments read
     * "1 / 2 Objekte verwendet" and could make no sense of it (PO). Exposed
     * here rather than re-derived in the banner, so the number and its noun
     * cannot disagree.
     */
    /** `combined` once the tenant has rooms and stations: the count holds both. */
    unit: 'stations' | 'rooms' | 'combined';
    /**
     * The limit is the tier of kolula's contract with the tenant, not the
     * trial's (ADR-0001 Amendment 6). Mutually exclusive with `isTrial`.
     */
    isContract: boolean;
}

const UNITS = ['rooms', 'stations', 'combined'] as const;

/**
 * The trial object limit, read from the API that enforces it (#2202,
 * `GET /admin/inventory/object-limit`). The count used to be computed here —
 * room products' objects or the depot list — while no endpoint checked it, so
 * the banner and the server could disagree. Now there is one definition
 * (`@kolula/shared` objectLimit.ts) and this only shows its numbers.
 *
 * Refetched on mount: the banner sits on list pages reached after a create
 * made elsewhere, and a stale count is the one thing it must not show.
 * Hidden (`isTrial` and `isContract` false) while loading, on a failure and
 * for a tenant without a limit — the API refuses beyond the limit either way,
 * and says why.
 */
export function useObjectLimit(): ObjectLimitState {
    const { data } = useGetObjectLimitQuery(undefined, { refetchOnMountOrArgChange: true });
    const counted = typeof data?.max === 'number' && typeof data?.current === 'number';
    const trial = counted && !!data?.isTrial;
    const contract = counted && !trial && data?.basis === 'contract';
    const limited = trial || contract;
    const current = limited ? (data?.current as number) : 0;
    const max = limited ? (data?.max as number) : Infinity;

    return {
        current,
        max,
        isAtLimit: current >= max,
        isTrial: trial,
        unit: UNITS.find((u) => u === data?.unit) ?? 'stations',
        isContract: contract,
    };
}
